// Translated label bundle for the DS-D `AccountCard` composite. The
// composite stays i18n-key-blind; this module produces the bundle from
// the `accounts` namespace via `useT()`.

import { useMemo } from 'react';

import { useT } from '@barel/i18n';
import type { AccountCardLabels, AccountStatus, OffTargetMode } from '@barel/ui';

export function useAccountCardLabels(): AccountCardLabels {
  const t = useT('accounts');
  return useMemo<AccountCardLabels>(
    () => ({
      status: {
        active: t('card.status.active'),
        paused: t('card.status.paused'),
        limited: t('card.status.limited'),
        error: t('card.status.error'),
        reconnecting: t('card.status.reconnecting'),
      },
      mode: {
        auto: t('card.mode.auto'),
        review: t('card.mode.review'),
        manual: t('card.mode.manual'),
      },
      businessesCount: (n: number) => t('card.businessesCount', { count: n }),
      campaignsCount: (n: number) => t('card.campaignsCount', { count: n }),
      offTarget: (mode: OffTargetMode) => {
        if (mode === 'block') return t('card.offTargetBlock');
        if (mode === 'suggest') return t('card.offTargetSuggest');
        return t('card.offTargetManual');
      },
      autoLeads: t('card.autoLeads'),
      salesNav: t('card.salesNav'),
      voicePrefix: t('card.voicePrefix'),
      actions: {
        pause: t('card.actions.pause'),
        resume: t('card.actions.resume'),
        reconnect: t('card.actions.reconnect'),
        autoResume: t('card.actions.autoResume'),
        reconnecting: t('card.actions.reconnecting'),
        editVoice: t('card.actions.editVoice'),
        disconnect: t('card.actions.disconnect'),
      },
      actionsAriaLabel: t('card.actionsAria'),
      autoResume: {
        tooltip: t('card.autoResume.tooltip'),
        duration: (msRemaining: number) => {
          // Render at minute resolution — the page-level ticker only fires
          // every minute, so sub-minute precision would noise without value.
          const totalMinutes = Math.max(0, Math.ceil(msRemaining / 60_000));
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes - hours * 60;
          const duration =
            hours > 0
              ? t('card.autoResume.durationHours', { hours, minutes })
              : t('card.autoResume.durationMinutes', { minutes });
          return t('card.autoResume.durationTemplate', { duration });
        },
      },
    }),
    [t],
  );
}

/** Default copy used when the row's `statusInfo` is null. */
export function useDefaultStatusInfo(): Partial<Record<AccountStatus, string>> {
  const t = useT('accounts');
  return useMemo<Partial<Record<AccountStatus, string>>>(
    () => ({
      paused: t('card.statusInfo.paused'),
      limited: t('card.statusInfo.limited'),
      error: t('card.statusInfo.error'),
      reconnecting: t('card.statusInfo.reconnecting'),
    }),
    [t],
  );
}
